<template>
  <v-layout>
    <slot
      name="activator"
      :initiative="selectedInitiative"
      :open="openDialog"
      :disabled="disabled"
      :loading="loadingSelected"
      :edit-url="selectedInitiativeUrl"
    >
      <v-flex class="d-flex flex-row align-end">
        <v-flex class="pr-2">
          <p v-if="label" class="text-caption mb-1 grey--text text--darken-1">
            {{ label }}
          </p>
          <v-card
            class="initiative-display-card"
            :class="disabled ? 'grey lighten-3' : ''"
            @click="openDialog"
          >
            <v-card-text class="py-2">
              <initiative-display
                v-if="!loading && hasSelectedInitiative"
                :initiative="selectedInitiative"
              ></initiative-display>
              <div v-else-if="loading" class="grey--text text-xs-center py-3">
                <v-progress-circular
                  color="primary"
                  indeterminate
                ></v-progress-circular>
              </div>
              <div v-else class="grey--text text-xs-center py-3">
                Clique para selecionar uma iniciativa
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-btn
          v-if="!hideEditButton"
          small
          icon
          target="_blank"
          title="Editar iniciativas"
          :to="selectedInitiativeUrl"
          style="max-width: 28px"
          class="mb-4"
        >
          <v-icon small>edit</v-icon>
        </v-btn>
      </v-flex>
    </slot>

    <!-- dialog de seleção -->
    <v-dialog v-model="dialogOpen" persistent max-width="max-content">
      <v-card class="initiative-selector-dialog">
        <v-card-title class="headline pb-1">
          Selecionar Iniciativa
          <v-spacer></v-spacer>
          <v-btn icon @click="handleClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- FILTROS -->
        <v-card-text class="pb-4 pt-0">
          <v-layout row wrap align-center>
            <v-flex xs12 sm4 class="pr-2">
              <v-text-field
                v-model="filterSearch"
                label="Pesquisar"
                append-icon="search"
                :clearable="true"
                hide-details
                box
                @input="handleSearchInput"
              >
              </v-text-field>
            </v-flex>

            <v-flex xs12 sm4 class="pr-2">
              <v-select
                v-model="filterCategory"
                :items="categoryOptions"
                label="Categoria"
                item-text="name"
                box
                item-value="value"
                :clearable="true"
                hide-details
                @change="resetAndFetch"
              >
              </v-select>
            </v-flex>

            <v-flex xs12 sm4>
              <sender-selector
                :items="senders"
                :loading="loadingSenders"
                v-model="filterSender"
                @input="resetAndFetch"
              >
              </sender-selector>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-divider class="ma-0"></v-divider>

        <!-- TABELA DE INICIATIVAS -->
        <v-card-text class="py-0 px-5 table-area">
          <v-data-table
            :items="initiatives"
            :total-items="totalItems"
            :headers="headers"
            :loading="loading"
            :pagination.sync="pagination"
            :rows-per-page-items="[10, 25, 50]"
            rows-per-page-text="Iniciativas por página"
            :disable-initial-sort="true"
            class="initiative-selector-table elevation-0"
            hide-headers
            @update:pagination="fetchInitiatives"
          >
            <template v-slot:items="props">
              <tr
                class="initiative-row"
                :title="props.item.name"
                @click="handleSelectItem(props.item)"
              >
                <td
                  class="px-0"
                  style="max-width: fit-content"
                  :class="{
                    'border-left-primary':
                      selectedInitiative &&
                      props.item.id === selectedInitiative.id,
                  }"
                >
                  <initiative-display
                    :initiative="props.item"
                    :is-selected="
                      selectedInitiative &&
                      props.item.id === selectedInitiative.id
                    "
                  ></initiative-display>
                </td>
              </tr>
            </template>

            <template v-slot:no-data>
              <div class="text-xs-center py-4 grey--text">
                Nenhuma iniciativa encontrada
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider class="ma-0"></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="handleClose">Cancelar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import initiativesService from "@/views/initiativeManager/service";
import WhatsAppService from "@/services/whatsapp";
import SenderSelector from "@/components/senderSelector.vue";
import ApprovalIcon from "@/components/ui/ApprovalIcon.vue";
import FlaggedIcon from "@/components/ui/FlaggedIcon.vue";
import QualityChip from "@/components/ui/QualityChip.vue";
import InitiativeDisplay from "@/components/ui/InitiativeDisplay.vue";

export default {
  name: "InitiativeSelectorDialog",
  components: {
    SenderSelector,
    ApprovalIcon,
    FlaggedIcon,
    QualityChip,
    InitiativeDisplay,
  },
  props: {
    value: {
      type: [Number, String, Object],
      default: null,
    },
    creditorId: {
      type: [Number, String],
      default: null,
    },
    scope: {
      type: String,
      default: null,
    },
    initialCategory: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: "Iniciativa do Whatsapp",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    restrictOwner: {
      type: String,
      default: null,
    },
    restrictCreditorId: {
      type: [Number, String],
      default: null,
    },
    hideEditButton: {
      type: Boolean,
      default: false,
    },
    excludeIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dialogOpen: false,
      currentInititive: null,
      selectedInitiative: null,
      loadingSelected: false,
      selectedRequestToken: 0,
      initiatives: [],
      totalItems: 0,
      loading: false,
      filterSearch: null,
      filterCategory: null,
      filterSender: null,
      senders: [],
      loadingSenders: false,
      searchTimer: null,
      pagination: {
        page: 1,
        rowsPerPage: 50,
        sortBy: "",
        descending: false,
      },
      headers: [
        {
          text: "Iniciativa",
          value: "id",
          sortable: false,
        },
      ],
      categoryOptions: [
        { name: "Marketing", value: "MARKETING" },
        { name: "Utilidade", value: "UTILITY" },
        { name: "Autenticação", value: "AUTHENTICATION" },
      ],
    };
  },
  computed: {
    hasSelectedInitiative() {
      return !!this.selectedInitiative;
    },
    selectedInitiativeUrl() {
      const key = this.selectedInitiative && this.selectedInitiative.key;
      if (!key) return "/initiatives";
      return `/initiatives?q=${encodeURIComponent(key)}`;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.syncSelectedInitiative(newValue);
      },
    },
    dialogOpen(isOpen) {
      if (!isOpen) return;
      this.filterCategory = this.initialCategory || null;
      this.filterSearch = null;
      this.filterSender = null;
      this.pagination.page = 1;
      this.fetchInitiatives();
      this.fetchSenders();
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    async syncSelectedInitiative(newInititiveId) {
      this.selectedRequestToken += 1;
      const requestToken = this.selectedRequestToken;

      if (typeof newInititiveId === "object") {
        this.selectedInitiative = newInititiveId;
        this.loadingSelected = false;
        return;
      }

      if (newInititiveId === undefined || newInititiveId === "") {
        this.selectedInitiative = null;
        this.loadingSelected = false;
        return;
      }

      if (
        this.selectedInitiative &&
        String(this.selectedInitiative.id) === String(newInititiveId)
      ) {
        this.loadingSelected = false;
        return;
      }

      this.loadingSelected = true;
      try {
        const response = await initiativesService.getInitiative(newInititiveId);
        if (requestToken !== this.selectedRequestToken) return;
        this.selectedInitiative =
          (response && response.data) || response || null;
      } catch (error) {
        if (requestToken !== this.selectedRequestToken) return;
        console.error("Erro ao carregar iniciativa selecionada:", error);
        this.selectedInitiative = null;
      } finally {
        if (requestToken === this.selectedRequestToken) {
          this.loadingSelected = false;
        }
      }
    },

    openDialog() {
      if (this.disabled) return;
      this.dialogOpen = true;
    },

    closeDialog() {
      this.dialogOpen = false;
    },

    buildParams() {
      const params = {
        limit: this.pagination.rowsPerPage || 25,
        page: this.pagination.page || 1,
        active: true,
        empty_creditor: true,
        meta_status: "APPROVED",
      };

      if (this.creditorId) params.creditor = this.creditorId;
      if (this.filterCategory) params.meta_category = this.filterCategory;
      if (this.filterSender) params.phone_sender_id = this.filterSender;
      if (this.filterSearch) params.search = this.filterSearch;
      if (this.restrictOwner) params.restrict_owner = this.restrictOwner;
      if (this.excludeIds && this.excludeIds.length) {
        params.exclude_ids = this.excludeIds.join(",");
      }
      if (this.scope) {
        params.scope = this.scope;
        params.empty_scope = true;
      }

      return params;
    },

    async fetchInitiatives() {
      if (this.loading) return;
      this.loading = true;
      try {
        const data = await initiativesService.getAllInitiatives(
          this.buildParams(),
        );
        this.initiatives = data.objects;
        this.totalItems = data.num_results;
      } catch (error) {
        console.error("Erro ao carregar iniciativas:", error);
        this.$store.commit(
          "showSnackbar",
          "Não foi possível carregar as iniciativas",
        );
      } finally {
        this.loading = false;
      }
    },

    fetchSenders() {
      if (this.loadingSenders || this.senders.length) return;
      this.loadingSenders = true;
      WhatsAppService.getSenders()
        .then((response) => {
          let senders = response.objects;
          if (this.restrictOwner) {
            senders = senders.filter((sender) => sender.owner === this.restrictOwner);
          }
          this.senders = senders.sort((a, b) => {
            if (a.phone_number < b.phone_number) return -1;
            if (a.phone_number > b.phone_number) return 1;
            return 0;
          });
        })
        .catch(() => {
          this.$store.commit(
            "showSnackbar",
            "Não foi possível buscar os remetentes do WhatsApp",
          );
        })
        .finally(() => {
          this.loadingSenders = false;
        });
    },

    resetAndFetch() {
      this.pagination.page = 1;
      this.fetchInitiatives();
    },

    handleSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.resetAndFetch, 500);
    },

    handleSelectItem(item) {
      const rejectionReason = this.getSelectionRejectionReason(item);
      if (rejectionReason) {
        this.$store.commit("showSnackbar", { message: rejectionReason, color: "error" });
        return;
      }
      this.selectedInitiative = item;
      this.$emit("input", item.id);
      this.$emit("select", item);
      this.closeDialog();
    },

    getSelectionRejectionReason(item) {
      if (this.restrictOwner && (!item.sender || item.sender.owner !== this.restrictOwner)) {
        return "A iniciativa de fallback precisa ser do mesmo broker da iniciativa principal";
      }
      if (
        this.restrictCreditorId &&
        item.creditor_id &&
        String(item.creditor_id) !== String(this.restrictCreditorId)
      ) {
        return "A iniciativa de fallback precisa ser do mesmo credor da iniciativa principal ou não ter credor específico";
      }
      if (this.excludeIds && this.excludeIds.includes(item.id)) {
        return "Essa iniciativa já foi escolhida em outro campo";
      }
      return null;
    },

    handleClose() {
      this.closeDialog();
      this.$emit("cancel");
    },
  },
};
</script>

<style lang="scss" scoped>
.border-left-primary {
  border-left: 4px solid #673ab7; //primary
}
::v-deep .v-datatable tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.initiative-display-card {
  cursor: pointer;
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(0.96);
  }
}

.initiative-selector-dialog {
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}

.table-area {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.initiative-selector-table {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;

  ::v-deep .v-table__overflow {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  ::v-deep .v-table__overflow thead th {
    background: white;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  ::v-deep .v-datatable__actions {
    flex-shrink: 0;
  }

  .initiative-row {
    cursor: pointer;
    height: 64px;

    td {
      font-size: 13px;
    }
  }
}

.initiative-status {
  border: 1px solid;
  border-radius: 2px;
  font-weight: 500;
  padding: 1px 4px;
  white-space: nowrap;
  font-size: 12px;

  &.APPROVED {
    background-color: #ebf5f1;
    border-color: #b6d1c6;
    color: #247058;
  }

  &.DISABLED,
  &.UNKNOWN {
    background-color: #e5e5e3;
    border-color: #a6a5a2;
    color: #545452;
  }

  &.PAUSED,
  &.FIRST_PAUSED,
  &.SECOND_PAUSED {
    background-color: #fff8e3;
    border-color: #ffe299;
    color: #ad7410;
  }

  &.PENDING_DELETION,
  &.REJECTED {
    background-color: #fff0f1;
    border-color: #facfd4;
    color: #ab1628;
  }
}
</style>
